import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { Connector, SetupContent, SetupGuideProps } from "../../types";
import { useState } from "react";

export const SetupGuide: React.FC<SetupGuideProps> = ({
  connector,
  onClose,
  onTestConnection,
}) => {
  const [isTesting, setIsTesting] = useState(false);
  const [testSuccessful, setTestSuccessful] = useState(false);

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setTestSuccessful(true);
      if (onTestConnection) {
        onTestConnection(true);
      }
    } catch (error) {
    
      setTestSuccessful(false);

      if (onTestConnection) {
        onTestConnection(false);
      }
    } finally {
      setIsTesting(false);
    }
  };

  const getSetupContent = (connector: Connector): SetupContent => {
    switch (connector.id) {
      case "postgres":
      case "postgres-dest":
        return {
          title: "Postgres",
          supportLevel: "AIRBYTE CONNECTOR",
          version: "2.4.7",
          lastUpdated: "6 days ago",
          syncRate: "99%",
          usageRate: "High",
          description:
            "This page guides you through the process of setting up the Postgres destination connector.",
          warning: {
            title: "Warning",
            content:
              "Postgres, while an excellent relational database, is not a data warehouse. Please only consider using postgres as a destination for small data volumes (e.g. less than 10GB) or for testing purposes. For larger data volumes, we recommend using a data warehouse like BigQuery, Snowflake, or Redshift.",
            link: "Learn more here",
          },
          fields: [
            {
              label: "Destination name",
              placeholder: "Postgres",
              type: "text",
            },
            { label: "Host", placeholder: "localhost", type: "text" },
            { label: "Port", placeholder: "5432", type: "number" },
            { label: "DB Name", placeholder: "postgres", type: "text" },
            { label: "Default Schema", placeholder: "public", type: "text" },
          ],
        };
      case "web-sdk":
        return {
          title: "Web SDK",
          supportLevel: "NATIVE CONNECTOR",
          version: "1.2.0",
          lastUpdated: "2 days ago",
          syncRate: "98%",
          usageRate: "Very High",
          description:
            "This page guides you through the process of setting up the Web SDK source connector for tracking web events.",
          warning: null,
          fields: [
            { label: "Source name", placeholder: "Web SDK", type: "text" },
            {
              label: "Write Key",
              placeholder: "Your write key",
              type: "password",
            },
            {
              label: "Tracking Domain",
              placeholder: "https://yourdomain.com",
              type: "url",
            },
            { label: "Enable Debug Mode", placeholder: "", type: "checkbox" },
          ],
        };
      case "mongodb":
        return {
          title: "MongoDB",
          supportLevel: "AIRBYTE CONNECTOR",
          version: "1.8.3",
          lastUpdated: "1 week ago",
          syncRate: "95%",
          usageRate: "Medium",
          description:
            "This page guides you through the process of setting up the MongoDB source connector.",
          warning: {
            title: "Performance Note",
            content:
              "MongoDB connectors may impact database performance during initial sync. We recommend running syncs during off-peak hours for production databases.",
            link: "Best practices guide",
          },
          fields: [
            { label: "Source name", placeholder: "MongoDB", type: "text" },
            {
              label: "Connection String",
              placeholder: "mongodb://localhost:27017",
              type: "text",
            },
            { label: "Database Name", placeholder: "mydb", type: "text" },
            {
              label: "Authentication Database",
              placeholder: "admin",
              type: "text",
            },
          ],
        };
      default:
        return {
          title: connector.name,
          supportLevel: "AIRBYTE CONNECTOR",
          version: "1.0.0",
          lastUpdated: "1 week ago",
          syncRate: "95%",
          usageRate: "Medium",
          description: `This page guides you through the process of setting up the ${connector.name} connector.`,
          warning: null,
          fields: [
            { label: "Name", placeholder: connector.name, type: "text" },
            {
              label: "Configuration",
              placeholder: "Enter configuration",
              type: "text",
            },
          ],
        };
    }
  };

  const setupData = getSetupContent(connector);

  return (
    <div className="w-full h-full bg-gray-900 text-white overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{connector.icon}</span>
          <h2 className="text-xl font-semibold">Setup Guide</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-3 py-1 bg-gray-800 rounded-md hover:bg-gray-700">
            <ExternalLink className="h-4 w-4" />
            <span className="text-sm">Open full docs</span>
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-800 rounded-md"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {setupData.title}
          </h1>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-300">Support Level:</span>
            <span className="text-blue-400 font-medium">
              {setupData.supportLevel}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-300">Connector Version:</span>
            <span className="text-white">
              {setupData.version}{" "}
              <span className="text-gray-400">
                (last updated {setupData.lastUpdated})
              </span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-300">Sync Success Rate:</span>
            <span className="text-green-400">{setupData.syncRate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-300">Usage Rate:</span>
            <span className="text-yellow-400">{setupData.usageRate}</span>
          </div>
        </div>

        {/* Description */}
        <div>
          <p className="text-gray-300 leading-relaxed">
            {setupData.description}
          </p>
        </div>

        {/* Warning */}
        {setupData.warning && (
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Warning</h3>
            <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-200 mb-2">
                    {setupData.warning.content}
                  </p>
                  <a
                    href="#"
                    className="text-blue-400 hover:text-blue-300 underline text-sm"
                  >
                    {setupData.warning.link}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Form */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-6">
            Configuration
          </h3>
          <div className="space-y-4">
            {setupData.fields.map((field, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {field.label}
                </label>
                {field.type === "checkbox" ? (
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-gray-300 text-sm">{field.label}</span>
                  </label>
                ) : (
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-gray-400"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Test Connection Status */}
        {testSuccessful && (
          <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-400 font-medium">
                Connection test successful!
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4 pt-6 border-t border-gray-700">
          <button
            onClick={handleTestConnection}
            disabled={isTesting}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {isTesting ? "Testing..." : "Test Connection"}
          </button>
          <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium">
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};
